import classNames from 'classnames';
import React from 'react';
import Icon from '@/components/atoms/icon/icon';
import Typography from '@/components/atoms/typography/typography';
import Tooltip from '@/components/molecules/tooltip/tooltip';
import UserAvatar, {
  BasicPerson,
} from '@/components/molecules/user-avatar/user-avatar';
import useCurrencyFormatter from '@/libraries/formatters/use-currency-formatter';
import { default as ExtraLegSpace } from './icons/extraLegSpace.svg';
import { default as ExtraLegSpaceInfant } from './icons/extraLegSpaceInfant.svg';
import { default as Infant } from './icons/infant.svg';
import { default as NoLean } from './icons/noLean.svg';
import styles from './plane-seat.module.scss';

export interface PlaneSeatProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /** The class name to apply */
  className?: string;
  /** Indicates if the seat is disabled */
  isDisabled?: boolean;
  /** The callback to handle the seat click */
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  /** Passenger data */
  passenger?: Content.Components['TransportPassengerDto'];
  /** Seat data */
  seat: Content.Components['SeatDto'] | Content.Components['SeatTypeDto'];
  /** Seat type */
  seatType?: Content.Components['SeatTypeDto'];
}

const isSeatType = (
  seat: Content.Components['SeatDto'] | Content.Components['SeatTypeDto'],
): seat is Content.Components['SeatTypeDto'] => {
  return 'imagePath' in seat;
};

type SeatVariant =
  | '1'
  | '32'
  | '33'
  | '42'
  | '46'
  | '47'
  | '49'
  | '50'
  | '58'
  | '59'
  | '72'
  | '77'
  | '80'
  | '92'
  | '93'
  | '101'
  | '120'
  | '121'
  | '122'
  | '123'
  | '124'
  | '127'
  | '128'
  | '129'
  | '130';

const variants: Record<SeatVariant, string> = {
  1: styles.isStandardInf,
  101: styles.isAdultOnly,
  120: styles.isStandard,
  121: styles.isRearRows,
  122: styles.isFrontCabin,
  123: styles.isExtraLegRoom,
  124: styles.isFrontCabin,
  127: styles.isExtraLegRoom,
  128: styles.isExtraLegRoom,
  129: styles.isRearRowsInf,
  130: styles.isFrontCabinInf,
  32: styles.isRearRows,
  33: styles.isFrontCabinInf,
  42: styles.isFrontCabinExtraLegRoom,
  46: styles.isStandard,
  47: styles.isExtraLegRoom,
  49: styles.isStandard,
  50: styles.isFrontCabin,
  58: styles.isNoLean,
  59: styles.isExtraLegRoom,
  72: styles.isStandard,
  77: styles.isRearRows,
  80: styles.isFrontCabinExtraLegRoom,
  92: styles.isFrontCabinExtraLegRoomInf,
  93: styles.isRearRowsInf,
};

const icons: Partial<
  Record<SeatVariant, React.FunctionComponent<React.SVGProps<SVGSVGElement>>>
> = {
  1: Infant,
  122: ExtraLegSpaceInfant,
  123: ExtraLegSpace,
  127: Infant,
  128: Infant,
  129: Infant,
  130: Infant,
  33: Infant,
  42: ExtraLegSpace,
  47: ExtraLegSpace,
  58: NoLean,
  59: ExtraLegSpace,
  72: Infant,
  80: ExtraLegSpace,
  92: ExtraLegSpaceInfant,
};

const PlaneSeat = ({
  className,
  isDisabled = false,
  onClick,
  seat,
  passenger,
  seatType,
  ...rest
}: PlaneSeatProps) => {
  const currencyFormatter = useCurrencyFormatter();
  const SeatIcon = icons[seat.seatTypeId as unknown as SeatVariant];

  const getBasicPerson = (
    passenger: Content.Properties.TransportPassenger,
  ): BasicPerson => {
    const { firstName, lastName, placeholders } = passenger;

    if (!firstName && !lastName) {
      return {
        firstName: placeholders.firstName,
        lastName: placeholders.lastName,
      };
    }

    return { firstName, lastName };
  };

  if (isSeatType(seat)) {
    return (
      <div
        className={classNames(
          styles.component,
          className,
          variants[seat.seatTypeId],
        )}
      >
        <div className={styles.seat}>
          {SeatIcon && <SeatIcon className={styles.icon} />}
        </div>
      </div>
    );
  }

  return (
    <button
      {...rest}
      className={classNames(
        styles.component,
        className,
        variants[seat.seatTypeId],
        {
          [styles.isOccupied]: seat.isOccupied,
          [styles.isSelected]: seat.isSelected,
          [styles.isDisabled]: isDisabled,
        },
      )}
      onClick={onClick}
    >
      {seat.isOccupied ? (
        <div className={styles.seat}>
          <Icon className={styles.icon} name="Close" size={20} />
        </div>
      ) : seat.isSelected && passenger ? (
        <div className={styles.seat}>
          <UserAvatar
            disableTooltipClick
            passenger={getBasicPerson(passenger)}
            size="small"
          />
        </div>
      ) : (
        <Tooltip
          content={
            <div className={styles.tooltipContent}>
              <Typography tagStyle="headingMedium">{seat.seatName}</Typography>
              {seatType && (
                <div className={styles.seatType}>
                  <Typography
                    className={styles.seatTypeName}
                    tagStyle="bodyMediumBold"
                  >
                    {seatType.name}
                  </Typography>

                  <Typography
                    className={styles.seatTypePrice}
                    tagStyle="bodySmall"
                  >
                    {currencyFormatter(seatType.price)}
                  </Typography>
                </div>
              )}
            </div>
          }
          useMinimumWidth={false}
          wrapChildrenOnly
        >
          <div className={styles.seat}>
            {SeatIcon && <SeatIcon className={styles.icon} />}
          </div>
        </Tooltip>
      )}
    </button>
  );
};

PlaneSeat.displayName = 'PlaneSeat';

export default PlaneSeat;
